import { ReactNode } from "react"
import dayjs from "dayjs"
import { Pencil } from "lucide-react"
import { P } from "../../../../auth/permissions"
import { useAbility } from "../../../../components/contexts/AbilityContext"
import BaseSection from "../../../../components/elements/BaseSection"
import { CostCenterType } from "../../../Mitarbeiter/types/Types"
import { ObjectType } from "../../types/types"

type ObjectDetailMasterDataType = {
    objectData: ObjectType
    costCenterData?: CostCenterType[]
    onEdit: () => void
}

const empty = "–"

const formatDate = (value?: string) => {
    if (!value) return ""
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format("DD.MM.YYYY") : ""
}

function LabeledFact(props: { label: string; children?: ReactNode }) {
    if (props.children == null || props.children === "" || props.children === empty) return null
    return (
        <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{props.label}</p>
            <p className="pt-0.5 text-sm 2xl:text-base break-words">{props.children}</p>
        </div>
    )
}

export default function ObjectDetailMasterData(props: ObjectDetailMasterDataType) {
    const ability = useAbility()
    const canEdit = ability.can(P.objekte_read_write.action, P.objekte_read_write.subject)
    const info = props.objectData.objectInfo
    const costCenterName = props.costCenterData?.find((item) => item._id === props.objectData.costCenter)?.name ?? ""
    const contractRange = [formatDate(info?.contractStart), formatDate(info?.contractEnd)].filter(Boolean).join(" – ")
    const units =
        info?.unitperentrance != null || info?.amount != null
            ? [info?.unitperentrance != null ? `${info.unitperentrance} je Eingang` : null, info?.amount != null ? `${info.amount} gesamt` : null]
                  .filter(Boolean)
                  .join(" · ")
            : ""

    return (
        <BaseSection classProps="!grow h-fit">
            <div className="flex items-center justify-between gap-3 pb-4">
                <p className="font-bold">Stammdaten</p>
                {canEdit ? (
                    <button
                        type="button"
                        aria-label="Bearbeiten"
                        onClick={props.onEdit}
                        className="btn btn-ghost btn-sm h-8 min-h-8 w-8 shrink-0 p-0 text-primary hover:bg-primary/10">
                        <Pencil className="h-4 w-4" />
                    </button>
                ) : null}
            </div>
            <div className="flex flex-col gap-5">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <LabeledFact label="Niederlassung">{costCenterName}</LabeledFact>
                    <LabeledFact label="Einheiten">{units}</LabeledFact>
                    <LabeledFact label="Fläche">{info?.propertyArea != null ? `${info.propertyArea} m²` : ""}</LabeledFact>
                    <LabeledFact label="Vertragslaufzeit">{contractRange}</LabeledFact>
                    <LabeledFact label="Letzte Preisanpassung">{formatDate(info?.lastPriceChange)}</LabeledFact>
                    <LabeledFact label="Letzte Vertragsänderung">{formatDate(info?.lastContractChange)}</LabeledFact>
                </div>
                {info?.notes ? (
                    <div className="pt-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Bemerkung</p>
                        <p className="pt-1 text-base leading-snug whitespace-pre-wrap break-words">{info.notes}</p>
                    </div>
                ) : null}
            </div>
        </BaseSection>
    )
}
